'use client';

/**
 * UploadOverlay
 *
 * Compact floating dark card (bottom-right) showing upload progress,
 * failure state with retry/discard, offline status, and completion.
 *
 * Mounted at the app root level (ProvidersWrapper or layout).
 * Orphan recovery is handled by UploadRecovery (upload-recovery.tsx).
 */

import { useEffect, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  AudioLines,
  WifiOff
} from 'lucide-react';
import { toast } from '@/lib/toast';
import { useQueryClient } from '@tanstack/react-query';
import { recordingKeys } from '@/lib/query-keys';
import {
  useRecordingUploadStore,
  useRecordingUploadActions
} from '@/lib/stores/recording-upload-store';
import {
  getRecordingMeta,
  deleteRecordingMeta,
  deleteChunksForRecording,
  deleteRecordingAction
} from '@/lib/services/recordings';
import { uploadRecording } from './upload-recording';

function formatEta(seconds: number): string {
  if (seconds < 60) return `~${seconds}s remaining`;
  return `~${Math.ceil(seconds / 60)}min remaining`;
}

export function UploadOverlay() {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const state = useRecordingUploadStore();
  const uploadActions = useRecordingUploadActions();

  // LF-3743: the mobile QR-handoff route shares this store (so it can
  // reuse the desktop upload pipeline + state) but renders its own
  // mobile-styled progress UI inline on the page. The desktop overlay
  // would render simultaneously with `matterId: 0` (the mobile sentinel)
  // and its "View" button would 404 to /matters/0/recordings. Suppress on
  // the public route — the page itself owns the upload UI there.
  const isMobileHandoffRoute = pathname?.startsWith('/device-recording');

  const hasPendingRecording = state.recordingId !== null;

  const isVisible =
    state.isUploading ||
    state.uploadFailed ||
    (state.isOffline && hasPendingRecording) ||
    state.percentComplete === 100;

  // Online/offline tracking
  useEffect(() => {
    const goOnline = () => uploadActions.setOffline(false);
    const goOffline = () => uploadActions.setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [uploadActions]);

  const handleRetry = useCallback(async () => {
    if (!state.matterId || !state.recordingId) return;

    uploadActions.retry();

    const _meta = await getRecordingMeta(state.recordingId);
    const result = await uploadRecording(state.recordingId, (progress) => {
      uploadActions.updateProgress(progress.loaded);
    });

    if (result.success) {
      uploadActions.complete();
      queryClient.invalidateQueries({
        queryKey: recordingKeys.list(state.matterId!)
      });
      setTimeout(() => uploadActions.reset(), 5000);
    } else {
      uploadActions.setFailed();
    }
  }, [state.matterId, state.recordingId, uploadActions, queryClient]);

  const handleDiscard = useCallback(async () => {
    if (!state.matterId || !state.recordingId) return;

    // Delete server recording + local data
    await deleteRecordingAction(state.recordingId).catch(() => {});
    await deleteChunksForRecording(state.recordingId).catch(() => {});
    await deleteRecordingMeta(state.recordingId).catch(() => {});

    queryClient.invalidateQueries({
      queryKey: recordingKeys.list(state.matterId!)
    });
    uploadActions.reset();
    toast('Recording discarded');
  }, [state.matterId, state.recordingId, uploadActions, queryClient]);

  const handleNavigate = useCallback(() => {
    if (state.matterId) {
      router.push(`/matters/${state.matterId}/recordings`);
    }
  }, [state.matterId, router]);

  if (isMobileHandoffRoute) return null;
  if (!isVisible) return null;

  return (
    <div className="dark border-border bg-card fixed right-4 bottom-4 z-50 w-72 rounded-lg border shadow-xl">
      {/* Header */}
      <div className="border-border/50 flex items-center justify-between border-b px-3 py-1.5">
        <div className="flex items-center gap-1.5">
          <AudioLines className="text-foreground-subtle h-3 w-3" />
          <span className="text-muted-foreground text-xs font-medium">
            AI Scribe
          </span>
          <span className="text-foreground-subtle">&middot;</span>
          <button
            type="button"
            onClick={handleNavigate}
            className="text-info hover:text-foreground inline-flex items-center gap-0.5 text-xs"
          >
            View <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-3 py-2.5">
        {state.isUploading && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-foreground-secondary text-xs">
                Uploading...
              </span>
              <span className="text-muted-foreground text-xs font-medium">
                {state.percentComplete}%
              </span>
            </div>
            <div className="bg-muted mt-1.5 h-1 overflow-hidden rounded-full">
              <div
                className="bg-info h-full rounded-full transition-[width] duration-300"
                style={{ width: `${state.percentComplete}%` }}
              />
            </div>
            <p className="text-12px text-foreground-subtle mt-1.5">
              {state.etaSeconds !== null
                ? `${formatEta(state.etaSeconds)} · `
                : ''}
              Don&apos;t close tab
            </p>
          </>
        )}

        {state.uploadFailed && (
          <div className="flex items-center gap-2">
            <div className="bg-error/15 flex h-5 w-5 shrink-0 items-center justify-center rounded-full">
              <AlertTriangle className="text-error h-3 w-3" />
            </div>
            <div className="flex-1">
              <p className="text-foreground text-xs font-medium">
                Upload failed
              </p>
              <p className="text-12px text-foreground-subtle">Saved locally</p>
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={handleRetry}
                className="text-12px bg-foreground text-background rounded px-2 py-1 font-medium"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={handleDiscard}
                className="text-12px bg-muted text-foreground-secondary hover:bg-foreground/15 rounded px-2 py-1 font-medium"
              >
                Discard
              </button>
            </div>
          </div>
        )}

        {state.isOffline && !state.uploadFailed && (
          <div className="flex items-center gap-2">
            <WifiOff className="text-foreground-subtle h-3.5 w-3.5 shrink-0" />
            <p className="text-muted-foreground text-xs">
              Offline, will upload on reconnect
            </p>
          </div>
        )}

        {!state.isUploading &&
          !state.uploadFailed &&
          state.percentComplete === 100 && (
            <div className="flex items-center gap-2">
              <CheckCircle2 className="text-success h-3.5 w-3.5 shrink-0" />
              <p className="text-foreground-secondary text-xs">
                Upload complete, now processing
              </p>
            </div>
          )}
      </div>
    </div>
  );
}
